import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, MapPin, Phone, Mail, Send, ShieldCheck } from "lucide-react";
import LitLabLayout from "../components/LitLabLayout";
import { litlabFormOptions, litlabSubmitQuote, litlabContactCaptcha, litlabSubmitContact } from "../api";

const INITIAL = {
  name: "", company: "", email: "", phone: "",
  state: "New York", country: "United States of America",
  industry: "", service: "", message: "", consent: false,
};

// ---------------- Quote form ----------------
export function LitLabQuote() {
  const [params] = useSearchParams();
  const [opts, setOpts] = useState({ states: [], countries: [], services: [], industries: [] });
  const [form, setForm] = useState({ ...INITIAL,
    service: params.get("service") || "",
    industry: params.get("industry") || "",
    message: params.get("test")
      ? `Hi L.I.T. team,\n\nI'd like a quote and turnaround for: ${params.get("test")}.\n\nPlease let me know sample requirements, deliverables and pricing.\n\nThanks,`
      : "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);
  useEffect(() => {
    document.title = "Request a Quote | L.I.T. Labs";
    litlabFormOptions().then(setOpts);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const r = await litlabSubmitQuote({ ...form,
        service: form.service || null, industry: form.industry || null });
      setDone(r);
    } catch (err) {
      setError(err?.response?.data?.detail || "Submit failed — please call us at 631-643-6792.");
    } finally { setBusy(false); }
  };

  return (
    <LitLabLayout>
      <section className="pt-16 pb-12 bg-white border-b border-sky-200">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-xs uppercase tracking-[0.3em] text-sky-700 font-bold">Quotes & questions</div>
          <h1 className="mt-3 litlab-serif text-4xl md:text-6xl font-bold text-slate-900 max-w-3xl">Tell us what you need.</h1>
          <p className="mt-5 text-slate-600 max-w-2xl">Most standard-test quotes go out the same business day. Rush turnarounds are a specialty — mention your deadline in the message.</p>
        </div>
      </section>

      <section className="py-16 bg-sky-50">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-3 gap-8" data-testid="litlab-quote">
          <div className="lg:col-span-2">
            {done ? (
              <div className="rounded-xl border border-emerald-400 bg-emerald-50 p-8" data-testid="litlab-quote-success">
                <CheckCircle2 className="w-10 h-10 text-emerald-600" />
                <h2 className="mt-4 litlab-serif text-2xl font-bold text-slate-900">Quote request received.</h2>
                <p className="mt-2 text-slate-700">{done.message}</p>
                <p className="mt-3 text-xs text-slate-500">Reference: <span className="font-mono">{done.id.slice(0, 12)}</span></p>
                <div className="mt-6 flex gap-3">
                  <Link to="/litlab" className="inline-flex items-center gap-2 border border-sky-300 bg-white hover:border-sky-500 text-slate-700 px-5 py-2.5 rounded font-semibold text-sm">Back to home</Link>
                  <a href="tel:631-643-6792" className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-white px-5 py-2.5 rounded font-bold text-sm shadow-sm">Call now</a>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="rounded-xl border border-sky-200 bg-white p-8 space-y-5 shadow-sm" data-testid="litlab-quote-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Your name *"><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="litlab-quote-name" className={FIELD} /></Field>
                  <Field label="Company"><input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="litlab-quote-company" className={FIELD} /></Field>
                  <Field label="Email *"><input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="litlab-quote-email" className={FIELD} /></Field>
                  <Field label="Phone"><input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="litlab-quote-phone" className={FIELD} /></Field>
                  <Field label="State">
                    <select value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} data-testid="litlab-quote-state" className={FIELD}>
                      <option value="">— select —</option>
                      {opts.states.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </Field>
                  <Field label="Country">
                    <select value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="litlab-quote-country" className={FIELD}>
                      {opts.countries.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </Field>
                  <Field label="Industry">
                    <select value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} data-testid="litlab-quote-industry" className={FIELD}>
                      <option value="">— any —</option>
                      {opts.industries.map((i) => <option key={i.slug} value={i.slug}>{i.label}</option>)}
                    </select>
                  </Field>
                  <Field label="Service">
                    <select value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} data-testid="litlab-quote-service" className={FIELD}>
                      <option value="">— any —</option>
                      {opts.services.map((s) => <option key={s.slug} value={s.slug}>{s.label}</option>)}
                    </select>
                  </Field>
                </div>
                <Field label="Describe your request *">
                  <textarea required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="litlab-quote-message" className={FIELD} placeholder="What are we testing, what standards, and what's the deadline?" />
                </Field>
                <label className="flex items-start gap-2 text-sm text-slate-600">
                  <input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} data-testid="litlab-quote-consent" className="mt-1 w-4 h-4 accent-sky-600" required />
                  <span>I consent to L.I.T. Labs contacting me about this request. My information will not be sold or shared.</span>
                </label>
                {error && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-300 rounded p-3">{error}</div>}
                <button type="submit" disabled={busy || !form.consent}
                  data-testid="litlab-quote-submit"
                  className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed text-white px-6 py-3 rounded font-bold transition shadow-sm">
                  <Send className="w-4 h-4" /> {busy ? "Sending…" : "Send request"}
                </button>
              </form>
            )}
          </div>
          <aside className="space-y-4">
            <div className="rounded-xl border border-sky-200 bg-white p-6 shadow-sm">
              <div className="text-xs uppercase tracking-[0.3em] text-sky-700 font-bold">Direct line</div>
              <div className="mt-4 space-y-3 text-sm">
                <div className="flex items-start gap-3"><Phone className="w-4 h-4 text-sky-700 flex-shrink-0 mt-0.5" /><a href="tel:631-643-6792" className="text-slate-700 hover:text-sky-700 font-mono">631-643-6792</a></div>
                <div className="flex items-start gap-3"><Mail className="w-4 h-4 text-sky-700 flex-shrink-0 mt-0.5" /><a href="mailto:rao@litlab.com" className="text-slate-700 hover:text-sky-700">rao@litlab.com</a></div>
                <div className="flex items-start gap-3"><MapPin className="w-4 h-4 text-sky-700 flex-shrink-0 mt-0.5" /><div className="text-slate-700">97 Marcus Blvd<br />Hauppauge, NY 11788</div></div>
              </div>
            </div>
            <div className="rounded-xl border border-emerald-400/50 bg-emerald-50 p-6">
              <div className="text-xs uppercase tracking-[0.3em] text-emerald-700 font-bold">Accreditations</div>
              <div className="mt-4 space-y-2 text-sm text-slate-700">
                <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Nadcap Materials Testing</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> AC7006 · ISO/IEC 17025 equiv.</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Operating since 1985</div>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </LitLabLayout>
  );
}

// ---------------- Contact page ----------------
const CONTACT_INITIAL = {
  name: "", email: "", phone: "", company: "",
  state: "", country: "United States of America",
  subject: "Materials testing enquiry", message: "",
  consent: true, website: "", captcha_answer: "",
};

export function LitLabContact() {
  const [opts, setOpts] = useState({ states: [], countries: [] });
  const [form, setForm] = useState(CONTACT_INITIAL);
  const [captcha, setCaptcha] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  const loadCaptcha = () => litlabContactCaptcha().then(setCaptcha).catch(() => setCaptcha(null));

  useEffect(() => {
    document.title = "Contact L.I.T. Labs";
    litlabFormOptions().then((d) => setOpts({ states: d.states, countries: d.countries }));
    loadCaptcha();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const r = await litlabSubmitContact({
        ...form,
        captcha_expected: captcha?.expected,
      });
      setDone(r);
    } catch (err) {
      setError(err?.response?.data?.detail || "Submit failed — please call us at 631-643-6792.");
      loadCaptcha();
      setForm((f) => ({ ...f, captcha_answer: "" }));
    } finally { setBusy(false); }
  };

  return (
    <LitLabLayout>
      <section className="pt-16 pb-12 bg-white border-b border-sky-200">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-xs uppercase tracking-[0.3em] text-sky-700 font-bold">Contact</div>
          <h1 className="mt-3 litlab-serif text-4xl md:text-6xl font-bold text-slate-900">Have any questions?</h1>
          <p className="mt-5 text-slate-600 max-w-2xl">One address, one number, one team of metallurgists. Send us a note — we reply within one business day.</p>
        </div>
      </section>

      <section className="py-14 bg-sky-50" data-testid="litlab-contact">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="rounded-xl border border-sky-200 bg-white p-6 shadow-sm" data-testid="litlab-contact-call">
            <Phone className="w-6 h-6 text-sky-700" />
            <div className="mt-4 text-xs uppercase tracking-widest text-slate-500 font-bold">Call us</div>
            <a href="tel:631-643-6792" className="mt-1 block font-mono text-xl text-slate-900 hover:text-sky-700">631-643-6792</a>
            <div className="mt-1 text-xs text-slate-500 font-mono">Fax: 631-643-5628</div>
          </div>
          <div className="rounded-xl border border-sky-200 bg-white p-6 shadow-sm" data-testid="litlab-contact-email">
            <Mail className="w-6 h-6 text-sky-700" />
            <div className="mt-4 text-xs uppercase tracking-widest text-slate-500 font-bold">Email</div>
            <a href="mailto:rao@litlab.com" className="mt-1 block text-xl text-slate-900 hover:text-sky-700">rao@litlab.com</a>
            <div className="mt-1 text-xs text-slate-500">Same-day quotes on standard tests</div>
          </div>
          <div className="rounded-xl border border-sky-200 bg-white p-6 shadow-sm" data-testid="litlab-contact-visit">
            <MapPin className="w-6 h-6 text-sky-700" />
            <div className="mt-4 text-xs uppercase tracking-widest text-slate-500 font-bold">Visit</div>
            <div className="mt-1 text-slate-800">97 Marcus Blvd<br />Hauppauge, NY 11788</div>
            <a href={`https://maps.google.com/?cid=8146543322811030322`} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-sky-700 hover:text-sky-800">Open in Google Maps <ArrowRight className="w-3 h-3" /></a>
          </div>
        </div>
      </section>

      {/* Contact form + Map */}
      <section className="py-16 bg-white border-t border-sky-200">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="text-xs uppercase tracking-[0.3em] text-sky-700 font-bold">Quotes & questions</div>
            <h2 className="mt-2 litlab-serif text-3xl md:text-4xl font-bold text-slate-900">Send us a message</h2>
            {done ? (
              <div className="mt-8 rounded-xl border border-emerald-400 bg-emerald-50 p-8" data-testid="litlab-contact-success">
                <CheckCircle2 className="w-10 h-10 text-emerald-600" />
                <h3 className="mt-4 litlab-serif text-2xl font-bold text-slate-900">Message received.</h3>
                <p className="mt-2 text-slate-700">{done.message}</p>
                <p className="mt-3 text-xs text-slate-500">Reference: <span className="font-mono">{done.id.slice(0, 12)}</span></p>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-6 rounded-xl border border-sky-200 bg-sky-50 p-6 md:p-8 space-y-5 shadow-sm" data-testid="litlab-contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Your name *">
                    <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="litlab-contact-name" className={FIELD} />
                  </Field>
                  <Field label="Company">
                    <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="litlab-contact-company" className={FIELD} />
                  </Field>
                  <Field label="Email *">
                    <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="litlab-contact-email-input" className={FIELD} />
                  </Field>
                  <Field label="Phone">
                    <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="litlab-contact-phone" className={FIELD} />
                  </Field>
                  <Field label="State">
                    <select value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} data-testid="litlab-contact-state" className={FIELD}>
                      <option value="">Select State</option>
                      <option value="Outside USA">Outside USA</option>
                      {opts.states.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </Field>
                  <Field label="Country">
                    <select value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="litlab-contact-country" className={FIELD}>
                      {opts.countries.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </Field>
                </div>
                <Field label="Subject">
                  <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="litlab-contact-subject" className={FIELD} />
                </Field>
                <Field label="Message *">
                  <textarea required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="litlab-contact-message" className={FIELD} placeholder="Tell us what you're testing, standards, deadline…" />
                </Field>

                {/* Honeypot — hidden from real users */}
                <div className="absolute -left-[10000px]" aria-hidden="true">
                  <label>Website (leave blank)
                    <input tabIndex={-1} autoComplete="off"
                      value={form.website}
                      onChange={(e) => setForm({ ...form, website: e.target.value })} />
                  </label>
                </div>

                {/* Simple math captcha — swap-in Google reCAPTCHA later */}
                <div className="rounded-lg border border-sky-300 bg-white p-4 flex items-center gap-4 flex-wrap"
                  data-testid="litlab-contact-captcha">
                  <div className="flex items-center gap-2 text-xs text-slate-600 font-semibold">
                    <ShieldCheck className="w-4 h-4 text-emerald-600" />
                    Spam check
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-700 font-mono">{captcha?.question || "…"} =</span>
                    <input type="text" required value={form.captcha_answer}
                      onChange={(e) => setForm({ ...form, captcha_answer: e.target.value })}
                      data-testid="litlab-contact-captcha-answer"
                      className="w-20 rounded bg-white border border-sky-300 focus:border-sky-600 outline-none px-2 py-1.5 text-sm text-slate-900" />
                    <button type="button" onClick={loadCaptcha}
                      className="text-xs text-sky-700 hover:text-sky-900 underline">Refresh</button>
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 ml-auto">Protected by challenge</div>
                </div>

                <label className="flex items-start gap-2 text-sm text-slate-600">
                  <input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} data-testid="litlab-contact-consent" className="mt-1 w-4 h-4 accent-sky-600" required />
                  <span>I consent to L.I.T. Labs contacting me about this enquiry. My information will not be sold or shared.</span>
                </label>
                {error && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-300 rounded p-3" data-testid="litlab-contact-error">{error}</div>}
                <button type="submit" disabled={busy || !form.consent}
                  data-testid="litlab-contact-submit"
                  className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed text-white px-6 py-3 rounded font-bold transition shadow-sm">
                  <Send className="w-4 h-4" /> {busy ? "Sending…" : "Send message"}
                </button>
              </form>
            )}
          </div>

          <aside className="space-y-4">
            <div className="rounded-xl overflow-hidden border border-sky-200 shadow-sm bg-white">
              <div className="aspect-video">
                <iframe
                  title="L.I.T. Labs location"
                  src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d3020.5!2d-73.187!3d40.827!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89e8304eb5b76ffd%3A0x710e55fd677b5b32!2sL.I.T.%20Labs%2C%20Inc.!5e0!3m2!1sen!2sus!4v1"
                  className="w-full h-full border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade" />
              </div>
              <div className="p-4">
                <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Our address</div>
                <div className="mt-2 text-sm text-slate-800">
                  L.I.T. Labs, Inc. — Since 1985<br />
                  Metallurgists · Analysts<br />
                  <span className="text-slate-500">(formerly Long Island Testing Laboratories, Inc.)</span><br />
                  97 Marcus Blvd.<br />
                  Hauppauge, NY 11788
                </div>
                <a href="tel:631-643-6792" className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-sky-700 hover:text-sky-800">631-643-6792</a>
              </div>
            </div>
            <div className="rounded-xl border border-emerald-400/50 bg-emerald-50 p-6">
              <div className="text-xs uppercase tracking-[0.3em] text-emerald-700 font-bold">Accreditations</div>
              <div className="mt-4 space-y-2 text-sm text-slate-700">
                <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Nadcap Materials Testing</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> AC7006 · ISO/IEC 17025 equiv.</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Operating since 1985</div>
              </div>
            </div>
          </aside>
        </div>

        <div className="max-w-6xl mx-auto px-6 mt-10">
          <Link to="/litlab/quote" data-testid="litlab-contact-quote"
            className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-white px-6 py-3 rounded font-bold transition shadow-sm">
            Or request a formal quote <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </LitLabLayout>
  );
}

const FIELD = "w-full rounded-lg bg-white border border-sky-300 focus:border-sky-600 focus:ring-1 focus:ring-sky-500 outline-none px-3 py-2.5 text-sm text-slate-900 transition";

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">{label}</div>
      {children}
    </label>
  );
}
